/**
 * AdminLegalizacaoPage - Dashboard de Legalizacao de Imoveis (AVCB e Alvaras/Licencas).
 *
 * Consome o dataset consolidado gerado por scripts/export_legalizacao_dashboard_json.py.
 * Acesso restrito a administradores do dominio interno (validado tambem no backend).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CircleDollarSign,
  FileWarning,
  Loader2,
  RefreshCw,
  ShieldAlert,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { API_BASE_URL } from '@/lib/api';
import { useAdminAuth } from '../context/AdminAuthContext';

/* ------------------------------------------------------------------ */
/*  Tipos                                                              */
/* ------------------------------------------------------------------ */

interface MsProposal {
  proposta: string;
  linhas: number;
  idsDistintos: number;
  total: number | null;
  pago: number | null;
  aberto: number | null;
  parcelaInicial: string;
  parcelaFinal: string;
  propostasAtiva: string;
  unidadesPd: string;
  avcbPd: string;
  observacao: string;
}

interface AtivaProposal {
  proposta: number;
  demanda: string;
  propostaMs: string;
  confianca: string;
  valorAtiva: number | null;
  msPagoEstimado: number | null;
  msAbertoEstimado: number | null;
  unidades: number;
  unidadesEncontradasPd: number;
  unidadesComAvcbPd: number;
  evidencia: string;
  observacoes: string;
}

interface PipelineRow {
  proposta: number;
  documento: string;
  origemUnidades: string;
  unidades: number;
  valorAtiva: number | null;
  propostaMs: string;
  coberturaMs: string;
  custoMsEstimado: number | null;
  margemEstimada: number | null;
  situacao: string;
}

interface AlvaraProposal {
  proposta: number;
  demanda: string;
  categorias: string[];
  anoEsperado: number;
  nfAtiva: string;
  valorDemanda: number | null;
  valorFaturado: number | null;
  valorAberto: number | null;
  statusFinanceiro: string;
  unidades: number;
  docsAlvoEncontrados: number;
  docsAlvoEsperados: number;
  docsRenovadosAno: number;
  resumoPd: string;
  observacoes: string;
}

interface DashboardData {
  generatedAt: string;
  cutoffDate: string;
  propertyDocs: { lastSyncAt: string | null; documents: number | null; units: number | null };
  avcb: {
    kpis: {
      propostasAnalisadas: number;
      propostasComVinculoMs: string;
      propostasMs: number;
      totalMs: number;
      pagoMs: number;
      abertoMs: number;
      unidadesPd: string;
      unidadesComAvcbPd: number;
      pipelinePropostas: number;
      pipelineValorAtiva: number;
      pipelineCustoMs: number;
    };
    msProposals: MsProposal[];
    ativaProposals: AtivaProposal[];
    pipeline: PipelineRow[];
    gaps: {
      semPdf: number[];
      valorDivergente: number[];
      semVinculoMs: number[];
      empateIds: Array<{ proposta: number; evidencia: string }>;
      msSemPropostaAtiva: string[];
    };
  };
  alvaras: {
    kpis: {
      propostas: number;
      propostasFaturadas: number;
      totalDemanda: number;
      totalFaturado: number;
      totalAberto: number;
      docsEncontradosPd: string;
      docsComEvidenciaAno: number;
      unidadesParaCruzar: number;
    };
    proposals: AlvaraProposal[];
    categorias: string[];
    gaps: {
      semPdf: number[];
      valorDivergente: number[];
      pdfSemPlanilha: number[];
      multiplosAlvaras: number[];
      lacunas: Array<{ tema: string; escopo: string; impacto: string }>;
    };
  };
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2'];

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatCompactCurrency(value: number): string {
  return `R$ ${(value / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString('pt-BR');
}

function KpiCard({
  title,
  value,
  hint,
  icon: Icon,
  tone = 'default',
}: {
  title: string;
  value: string;
  hint?: string;
  icon: typeof Wallet;
  tone?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-emerald-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',
  }[tone];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${toneClass}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Pagina                                                             */
/* ------------------------------------------------------------------ */

export function AdminLegalizacaoPage() {
  const { token } = useAdminAuth();
  const { toast } = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/claro/api/legalizacao/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const json = await response.json().catch(() => null);

      if (!response.ok || json?.status === 'error') {
        throw new Error(json?.message || `HTTP ${response.status}`);
      }
      setData(json.data as DashboardData);
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Erro desconhecido';
      setError(message);
      toast({ title: 'Erro ao carregar o dashboard', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const msChartData = useMemo(() => {
    if (!data) return [];
    return data.avcb.msProposals.map((row) => ({
      proposta: row.proposta,
      Pago: row.pago ?? 0,
      Aberto: row.aberto ?? 0,
    }));
  }, [data]);

  const categoriaChartData = useMemo(() => {
    if (!data) return [];
    const counter = new Map<string, number>();
    data.alvaras.proposals.forEach((proposal) => {
      proposal.categorias.forEach((categoria) => {
        counter.set(categoria, (counter.get(categoria) ?? 0) + 1);
      });
    });
    return Array.from(counter, ([name, value]) => ({ name, value }));
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" />
            Nao foi possivel carregar o dashboard
          </CardTitle>
          <CardDescription>{error ?? 'Dataset indisponivel.'}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => void loadDashboard()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Tentar novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { avcb, alvaras } = data;
  const totalAberto = avcb.kpis.abertoMs + alvaras.kpis.totalAberto;

  return (
    <div className="space-y-6">
      {/* Cabecalho */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Legalizacao de Imoveis</h1>
          <p className="text-sm text-muted-foreground">
            Consolidacao das analises de AVCB (Ativa x MS/PPCI) e Alvaras/Licencas contra o PropertyDocs.
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant="outline">Dataset: {formatDateTime(data.generatedAt)}</Badge>
            <Badge variant="outline">Corte: {new Date(data.cutoffDate).toLocaleDateString('pt-BR')}</Badge>
            <Badge variant="outline">
              PropertyDocs: {data.propertyDocs.documents?.toLocaleString('pt-BR') ?? '—'} documentos
            </Badge>
            <Badge variant="outline">Sync: {formatDateTime(data.propertyDocs.lastSyncAt)}</Badge>
          </div>
        </div>
        <Button onClick={() => void loadDashboard()} variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </div>

      <Tabs defaultValue="visao-geral" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="visao-geral">Visao geral</TabsTrigger>
          <TabsTrigger value="avcb">AVCB</TabsTrigger>
          <TabsTrigger value="alvaras">Alvaras e Licencas</TabsTrigger>
          <TabsTrigger value="pipeline">
            Pipeline
            {avcb.kpis.pipelinePropostas > 0 && (
              <Badge variant="secondary" className="ml-2">
                {avcb.kpis.pipelinePropostas}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="lacunas">Lacunas</TabsTrigger>
        </TabsList>

        {/* ---------------------------- Visao geral ---------------------------- */}
        <TabsContent value="visao-geral" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Total contratado MS"
              value={formatCurrency(avcb.kpis.totalMs)}
              hint={`${avcb.kpis.propostasMs} propostas MS/PPCI`}
              icon={CircleDollarSign}
            />
            <KpiCard
              title="Ja pago a MS"
              value={formatCurrency(avcb.kpis.pagoMs)}
              hint="Parcelas com NFe ou data de faturamento"
              icon={Wallet}
              tone="success"
            />
            <KpiCard
              title="Em aberto para MS"
              value={formatCurrency(avcb.kpis.abertoMs)}
              hint="Repasse pendente ao fornecedor"
              icon={AlertTriangle}
              tone="warning"
            />
            <KpiCard
              title="Pipeline a faturar"
              value={formatCurrency(avcb.kpis.pipelineValorAtiva)}
              hint={`${avcb.kpis.pipelinePropostas} propostas sem faturamento lancado`}
              icon={TrendingUp}
              tone="danger"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Alvaras/Licencas - demanda"
              value={formatCurrency(alvaras.kpis.totalDemanda)}
              hint={`${alvaras.kpis.propostas} propostas no escopo`}
              icon={Building2}
            />
            <KpiCard
              title="Alvaras/Licencas - faturado"
              value={formatCurrency(alvaras.kpis.totalFaturado)}
              hint={`${alvaras.kpis.propostasFaturadas} de ${alvaras.kpis.propostas} a 100%`}
              icon={Wallet}
              tone="success"
            />
            <KpiCard
              title="Alvaras/Licencas - aberto"
              value={formatCurrency(alvaras.kpis.totalAberto)}
              hint="Valor a faturar conforme planilha"
              icon={AlertTriangle}
              tone="warning"
            />
            <KpiCard
              title="Exposicao total em aberto"
              value={formatCurrency(totalAberto)}
              hint="MS em aberto + Alvaras a faturar"
              icon={FileWarning}
              tone="danger"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Financeiro por Proposta MS</CardTitle>
                <CardDescription>Valor ja pago e valor em aberto para o fornecedor MS/PPCI.</CardDescription>
              </CardHeader>
              <CardContent className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={msChartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="proposta" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={formatCompactCurrency} width={70} />
                    <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="Pago" stackId="a" fill="#16a34a" />
                    <Bar dataKey="Aberto" stackId="a" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Alvaras por categoria</CardTitle>
                <CardDescription>Distribuicao das propostas no escopo.</CardDescription>
              </CardHeader>
              <CardContent className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={categoriaChartData} dataKey="value" nameKey="name" outerRadius={80} label>
                      {categoriaChartData.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(value: number, name: string) => [`${value} propostas`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ------------------------------- AVCB -------------------------------- */}
        <TabsContent value="avcb" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Financeiro por Proposta MS</CardTitle>
              <CardDescription>
                Leitura mais precisa: a base MS/PPCI organiza o faturamento por proposta e por unidade.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[420px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proposta MS</TableHead>
                      <TableHead className="text-right">Linhas</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Pago</TableHead>
                      <TableHead className="text-right">Aberto</TableHead>
                      <TableHead>50% inicial</TableHead>
                      <TableHead>50% final</TableHead>
                      <TableHead>Propostas Ativa</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {avcb.msProposals.map((row) => (
                      <TableRow key={row.proposta}>
                        <TableCell className="font-medium">{row.proposta}</TableCell>
                        <TableCell className="text-right">{row.linhas}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.total)}</TableCell>
                        <TableCell className="text-right text-emerald-500">{formatCurrency(row.pago)}</TableCell>
                        <TableCell className="text-right text-amber-500">{formatCurrency(row.aberto)}</TableCell>
                        <TableCell className="text-xs">{row.parcelaInicial}</TableCell>
                        <TableCell className="text-xs">{row.parcelaFinal}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-xs" title={row.propostasAtiva}>
                          {row.propostasAtiva}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Propostas Ativa ({avcb.ativaProposals.length})</CardTitle>
              <CardDescription>Vinculo com a MS, valores e cobertura no PropertyDocs.</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[460px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ativa</TableHead>
                      <TableHead>MS</TableHead>
                      <TableHead>Confianca</TableHead>
                      <TableHead className="text-right">Valor Ativa</TableHead>
                      <TableHead className="text-right">Unidades</TableHead>
                      <TableHead className="text-right">AVCB no PD</TableHead>
                      <TableHead>Evidencia</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {avcb.ativaProposals.map((row) => (
                      <TableRow key={row.proposta}>
                        <TableCell className="font-medium">{row.proposta}</TableCell>
                        <TableCell>{row.propostaMs}</TableCell>
                        <TableCell>
                          <Badge variant={row.confianca === 'Forte' ? 'default' : 'secondary'}>{row.confianca}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(row.valorAtiva)}</TableCell>
                        <TableCell className="text-right">{row.unidades}</TableCell>
                        <TableCell className="text-right">
                          {row.unidadesComAvcbPd}/{row.unidadesEncontradasPd}
                        </TableCell>
                        <TableCell className="max-w-[320px] truncate text-xs" title={row.evidencia}>
                          {row.evidencia}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --------------------------- Alvaras --------------------------------- */}
        <TabsContent value="alvaras" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <KpiCard
              title="Documentos no PropertyDocs"
              value={alvaras.kpis.docsEncontradosPd}
              hint="Documentos alvo encontrados"
              icon={Building2}
            />
            <KpiCard
              title="Com evidencia do ano"
              value={String(alvaras.kpis.docsComEvidenciaAno)}
              hint="Renovado/concluido no ano esperado"
              icon={TrendingUp}
              tone="success"
            />
            <KpiCard
              title="Unidades/CNPJs cruzados"
              value={String(alvaras.kpis.unidadesParaCruzar)}
              hint="Chaves usadas no cruzamento"
              icon={FileWarning}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Propostas de Alvaras e Licencas ({alvaras.proposals.length})</CardTitle>
              <CardDescription>Situacao financeira e cobertura documental por proposta.</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[520px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proposta</TableHead>
                      <TableHead>Categorias</TableHead>
                      <TableHead>Ano</TableHead>
                      <TableHead>NF</TableHead>
                      <TableHead className="text-right">Demanda</TableHead>
                      <TableHead className="text-right">Faturado</TableHead>
                      <TableHead className="text-right">Aberto</TableHead>
                      <TableHead className="text-right">Docs PD</TableHead>
                      <TableHead className="text-right">Renovados</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alvaras.proposals.map((row) => (
                      <TableRow key={row.proposta}>
                        <TableCell className="font-medium">{row.proposta}</TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs" title={row.categorias.join(', ')}>
                          {row.categorias.join(', ')}
                        </TableCell>
                        <TableCell>{row.anoEsperado}</TableCell>
                        <TableCell className="text-xs">{row.nfAtiva || '—'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(row.valorDemanda)}</TableCell>
                        <TableCell className="text-right text-emerald-500">{formatCurrency(row.valorFaturado)}</TableCell>
                        <TableCell className="text-right">
                          {row.valorAberto && row.valorAberto > 0 ? (
                            <span className="text-amber-500">{formatCurrency(row.valorAberto)}</span>
                          ) : (
                            formatCurrency(row.valorAberto)
                          )}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {row.docsAlvoEncontrados}/{row.docsAlvoEsperados}
                        </TableCell>
                        <TableCell className="text-right text-xs">{row.docsRenovadosAno}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --------------------------- Pipeline -------------------------------- */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <KpiCard
              title="A faturar ao cliente"
              value={formatCurrency(avcb.kpis.pipelineValorAtiva)}
              hint="Receita ainda nao lancada na planilha Ativa"
              icon={TrendingUp}
              tone="danger"
            />
            <KpiCard
              title="Custo MS estimado"
              value={formatCurrency(avcb.kpis.pipelineCustoMs)}
              hint="Unidades x valor unitario MS"
              icon={Wallet}
              tone="warning"
            />
            <KpiCard
              title="Margem estimada"
              value={formatCurrency(avcb.kpis.pipelineValorAtiva - avcb.kpis.pipelineCustoMs)}
              hint="Diferenca entre receita e custo estimado"
              icon={CircleDollarSign}
              tone="success"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Propostas com documento, sem faturamento lancado</CardTitle>
              <CardDescription>
                Trabalho ja contratado e/ou com base operacional na MS que ainda nao virou receita nem repasse.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ativa</TableHead>
                    <TableHead className="text-right">Unidades</TableHead>
                    <TableHead>Origem das unidades</TableHead>
                    <TableHead>MS</TableHead>
                    <TableHead>Cobertura</TableHead>
                    <TableHead className="text-right">Valor Ativa</TableHead>
                    <TableHead className="text-right">Custo MS est.</TableHead>
                    <TableHead className="text-right">Margem est.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {avcb.pipeline.map((row) => (
                    <TableRow key={row.proposta}>
                      <TableCell className="font-medium">{row.proposta}</TableCell>
                      <TableCell className="text-right">{row.unidades || '—'}</TableCell>
                      <TableCell className="text-xs">{row.origemUnidades}</TableCell>
                      <TableCell>{row.propostaMs}</TableCell>
                      <TableCell className="text-xs">{row.coberturaMs}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.valorAtiva)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(row.custoMsEstimado)}</TableCell>
                      <TableCell className="text-right text-emerald-500">{formatCurrency(row.margemEstimada)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------------------- Lacunas -------------------------------- */}
        <TabsContent value="lacunas" className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-500" />
                  AVCB
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <GapLine
                  label="Propostas MS sem Proposta Ativa vinculada"
                  values={avcb.gaps.msSemPropostaAtiva}
                  hint="Requer confirmacao com o cliente."
                />
                <Separator />
                <GapLine label="Propostas Ativa sem documento" values={avcb.gaps.semPdf.map(String)} />
                <Separator />
                <GapLine label="Valor do documento divergente da planilha" values={avcb.gaps.valorDivergente.map(String)} />
                <Separator />
                <GapLine label="Sem vinculo MS (fornecedor PMC)" values={avcb.gaps.semVinculoMs.map(String)} />
                <Separator />
                <div>
                  <p className="font-medium">Empate de IDs no vinculo MS</p>
                  {avcb.gaps.empateIds.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum</p>
                  ) : (
                    avcb.gaps.empateIds.map((item) => (
                      <p key={item.proposta} className="mt-1 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{item.proposta}:</span> {item.evidencia}
                      </p>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-amber-500" />
                  Alvaras e Licencas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <GapLine
                  label="Unidades com mais de um alvara ativo"
                  values={alvaras.gaps.multiplosAlvaras.map(String)}
                  hint="CNPJs distintos no mesmo endereco; cada documento e contado separadamente."
                />
                <Separator />
                <GapLine label="Documento sem linha na planilha" values={alvaras.gaps.pdfSemPlanilha.map(String)} />
                <Separator />
                <GapLine label="Valor do documento divergente" values={alvaras.gaps.valorDivergente.map(String)} />
                <Separator />
                <GapLine label="Propostas sem documento local" values={alvaras.gaps.semPdf.map(String)} />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Lacunas estruturais de dados</CardTitle>
              <CardDescription>Informacoes que nao existem nas bases disponibilizadas.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tema</TableHead>
                    <TableHead>Escopo</TableHead>
                    <TableHead>Impacto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alvaras.gaps.lacunas.map((row) => (
                    <TableRow key={row.tema}>
                      <TableCell className="font-medium">{row.tema}</TableCell>
                      <TableCell className="text-xs">{row.escopo}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{row.impacto}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GapLine({ label, values, hint }: { label: string; values: string[]; hint?: string }) {
  return (
    <div>
      <p className="font-medium">{label}</p>
      {values.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhuma</p>
      ) : (
        <div className="mt-1 flex flex-wrap gap-1">
          {values.map((value) => (
            <Badge key={value} variant="secondary">
              {value}
            </Badge>
          ))}
        </div>
      )}
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
